import { useState, useRef, useCallback, useEffect } from "react";

/**
 * Encapsulates MediaRecorder and recording timer. Returns controls and state.
 * @param {React.RefObject} videoRef - ref to video or { current: { video, srcObject } }
 * @param {(file: File) => void} onVideoComplete - called when recording stops with the resulting file
 */
export function useRecording(videoRef, onVideoComplete) {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingStartTime, setRecordingStartTime] = useState(null);
  const [recordingElapsedMs, setRecordingElapsedMs] = useState(0);
  const mediaRecorderRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const recordingIntervalRef = useRef(null);
  const onCompleteRef = useRef(onVideoComplete);
  onCompleteRef.current = onVideoComplete;

  const startRecording = useCallback(() => {
    const node = videoRef.current;
    const videoEl = node?.video ?? node;
    const stream = videoEl?.srcObject;
    if (
      !stream ||
      typeof stream.getVideoTracks !== "function" ||
      stream.getVideoTracks().length === 0
    )
      return;
    if (typeof MediaRecorder === "undefined") return;
    const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp8")
      ? "video/webm;codecs=vp8"
      : MediaRecorder.isTypeSupported("video/webm")
        ? "video/webm"
        : MediaRecorder.isTypeSupported("video/mp4")
          ? "video/mp4"
          : "";
    recordingChunksRef.current = [];
    const options = mimeType
      ? { mimeType, videoBitsPerSecond: 2500000 }
      : { videoBitsPerSecond: 2500000 };
    let recorder;
    try {
      recorder = new MediaRecorder(stream, options);
    } catch {
      return;
    }
    const resolvedType = recorder.mimeType || mimeType || "video/webm";
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) recordingChunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      const chunks = recordingChunksRef.current;
      if (chunks.length === 0) return;
      const blob = new Blob(chunks, { type: resolvedType });
      if (blob.size === 0) return;
      const ext = resolvedType.includes("mp4") ? "mp4" : "webm";
      const file = new File([blob], `video_${Date.now()}.${ext}`, { type: resolvedType });
      onCompleteRef.current?.(file);
      mediaRecorderRef.current = null;
    };
    mediaRecorderRef.current = recorder;
    recorder.start(250);
    setRecordingStartTime(Date.now());
    setRecordingElapsedMs(0);
    setIsRecording(true);
  }, [videoRef]);

  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== "recording") return;
    try {
      recorder.requestData();
    } catch (_) {}
    recorder.stop();
    if (recordingIntervalRef.current) clearInterval(recordingIntervalRef.current);
    setIsRecording(false);
    setRecordingStartTime(null);
  }, []);

  useEffect(() => {
    if (!isRecording || recordingStartTime == null) return;
    recordingIntervalRef.current = setInterval(() => {
      setRecordingElapsedMs(Date.now() - recordingStartTime);
    }, 1000);
    return () => {
      if (recordingIntervalRef.current) clearInterval(recordingIntervalRef.current);
    };
  }, [isRecording, recordingStartTime]);

  useEffect(() => {
    return () => {
      const rec = mediaRecorderRef.current;
      if (rec && rec.state !== "inactive") {
        try {
          rec.stop();
        } catch (_) {}
      }
    };
  }, []);

  return { isRecording, recordingElapsedMs, startRecording, stopRecording };
}
